import { Container, createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import React, { useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import Sidebar from "./components/Sidebar";
import CalculatorPage from "./pages/calculator/CalculatorPage";
import CheckoutsPage from './pages/checkouts/CheckoutsPage';
import EditCheckoutPage from "./pages/checkouts/EditCheckoutPage";
import QuickCheckoutPage from "./pages/checkouts/QuickCheckoutPage";
import RegisterCheckoutPage from "./pages/checkouts/RegisterCheckoutPage";
import RegisterBatchCheckoutPage from "./pages/checkouts/RegisterBatchCheckoutPage";
import EditCustomerPage from "./pages/customers/EditCustomerPage";
import ListCustomersPage from "./pages/customers/ListCustomersPage";
import RegisterCustomerPage from "./pages/customers/RegisterCustomerPage";
import DashboardPage from './pages/DashboardPage';
import ForgotPassword from "./pages/ForgotPassword";
import AccountConfirmation from "./pages/front/AccountConfirmation";
import HomePage from "./pages/front/HomePage";
import PasswordRedefinition from "./pages/front/PasswordRedefinition";
import PrivacyPolicyPage from "./pages/front/PrivacyPolicyPage";
import TermsOfUsePage from "./pages/front/TermsOfUsePage";
import CalculatorPageFront from "./pages/front/PublicCalculatorPage";
import LoginPage from './pages/LoginPage';
import EditMaterialPage from "./pages/materials/EditMaterialPage";
import ListMaterialsPage from './pages/materials/ListMaterialsPage';
import RegisterMaterialPage from "./pages/materials/RegisterMaterialPage";
import MyAccountPage from "./pages/my-account/MyAccountPage";
import SalesChannelsPage from "./pages/my-account/SalesChannelsPage";
import PaymentFeesPage from "./pages/my-account/PaymentFeesPage";
import IntegrationsPage from "./pages/integrations/IntegrationsPage";
import OrderDetailsPage from "./pages/orders/OrderDetailsPage";
import OrdersPage from "./pages/orders/OrdersPage";
import EditJobPage from "./pages/jobs/EditJobPage";
import ListJobsPage from "./pages/jobs/ListJobsPage";
import RegisterJobPage from "./pages/jobs/RegisterJobPage";
import EditPrinterMaintenancePage from "./pages/printer-maintenances/EditPrinterMaintenancePage";
import ListPrinterMaintenancesPage from "./pages/printer-maintenances/ListPrinterMaintenancesPage";
import RegisterPrinterMaintenancePage from "./pages/printer-maintenances/RegisterPrinterMaintenancePage";
import EditPrinterPage from "./pages/printers/EditPrinterPage";
import ListPrintersPage from './pages/printers/ListPrintersPage';
import RegisterPrinterPage from "./pages/printers/RegisterPrinterPage";
import EditProductPage from "./pages/products/EditProductPage";
import ProductsPage from './pages/products/ProductsPage';
import RegisterProductPage from "./pages/products/RegisterProductPage";
import CreateTransactionPage from './pages/transactions/CreateTransactionPage';
import EditTransactionPage from './pages/transactions/EditTransactionPage';
import TransactionsPage from './pages/transactions/TransactionsPage';
import SettlementImportPage from './pages/transactions/SettlementImportPage';
import { hasPremiumAccess, readStoredPlanName } from './helpers/subscription';
import { persistDarkModePreference, readDarkModePreference } from './helpers/themePreference';
import useLiveNotifications from './hooks/useLiveNotifications';

function PremiumRoute({ children }) {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
        return <Navigate to="/admin/login" />;
    }
    if (!hasPremiumAccess(readStoredPlanName())) {
        return <Navigate to="/admin" replace />;
    }
    return children;
}
function PrivateRoute({ children }) {
    const accessToken = localStorage.getItem('access_token');
    return accessToken ? children : <Navigate to="/admin/login" />;
}

// Rotas administrativas encapsuladas no caminho "/admin"
function AdminRoutes() {
    const [sidebarOpen, setSidebarOpen] = useState(false); // Controle da visibilidade da Sidebar
    const [darkMode, setDarkMode] = useState(readDarkModePreference);

    const location = useLocation();
    const isLoginPage = location.pathname === '/admin/login';
    const isForgotPasswordPage = location.pathname === '/admin/forgot-password';
    const isPublicAuthPage = isLoginPage || isForgotPasswordPage;
    const liveNotificationsEnabled = !isPublicAuthPage && Boolean(localStorage.getItem('access_token'));

    useLiveNotifications(liveNotificationsEnabled);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen); // Alterna entre aberto e fechado
    };

    const toggleDarkMode = () => {
        const newDarkMode = !darkMode;
        setDarkMode(newDarkMode);
        persistDarkModePreference(newDarkMode);
    };

    // Cria o tema dinâmico com base no estado do darkMode
    const theme = createTheme({
        palette: {
            mode: (darkMode && !isPublicAuthPage) ? 'dark' : 'light',
        },
    });

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {!isLoginPage && !isForgotPasswordPage &&
                <Header toggleSidebar={toggleSidebar} toggleDarkMode={toggleDarkMode} darkMode={darkMode} />}
            <Sidebar open={sidebarOpen} onClose={toggleSidebar} darkMode={darkMode} />
            <div style={{ display: 'flex' }}>
                <Container component="main" style={{ flexGrow: 1, padding: '20px' }}>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route
                            path="/"
                            element={
                                <PrivateRoute>
                                    <DashboardPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/my-account"
                            element={
                                <PrivateRoute>
                                    <MyAccountPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/sales-channels"
                            element={
                                <PrivateRoute>
                                    <SalesChannelsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/payment-fees"
                            element={
                                <PrivateRoute>
                                    <PaymentFeesPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/integrations"
                            element={
                                <PrivateRoute>
                                    <IntegrationsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/printers"
                            element={
                                <PrivateRoute>
                                    <ListPrintersPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-printer"
                            element={
                                <PrivateRoute>
                                    <RegisterPrinterPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-printer/:id"
                            element={
                                <PrivateRoute>
                                    <EditPrinterPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/jobs"
                            element={
                                <PrivateRoute>
                                    <ListJobsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-job"
                            element={
                                <PrivateRoute>
                                    <RegisterJobPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-job/:id"
                            element={
                                <PrivateRoute>
                                    <EditJobPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/printer-maintenances"
                            element={
                                <PrivateRoute>
                                    <ListPrinterMaintenancesPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-printer-maintenance"
                            element={
                                <PrivateRoute>
                                    <RegisterPrinterMaintenancePage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-printer-maintenance/:id"
                            element={
                                <PrivateRoute>
                                    <EditPrinterMaintenancePage />
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/customers"
                            element={
                                <PrivateRoute>
                                    <ListCustomersPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-customer"
                            element={
                                <PrivateRoute>
                                    <RegisterCustomerPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-customer/:id"
                            element={
                                <PrivateRoute>
                                    <EditCustomerPage />
                                </PrivateRoute>
                            }
                        />

                        <Route
                            path="/materials"
                            element={
                                <PrivateRoute>
                                    <ListMaterialsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-material"
                            element={
                                <PrivateRoute>
                                    <RegisterMaterialPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-material/:id"
                            element={
                                <PrivateRoute>
                                    <EditMaterialPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/products"
                            element={
                                <PrivateRoute>
                                    <ProductsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-product"
                            element={
                                <PrivateRoute>
                                    <RegisterProductPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-product/:id"
                            element={
                                <PrivateRoute>
                                    <EditProductPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/checkouts"
                            element={
                                <PrivateRoute>
                                    <CheckoutsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-checkout"
                            element={
                                <PrivateRoute>
                                    <RegisterCheckoutPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register-batch-checkout"
                            element={
                                <PrivateRoute>
                                    <PremiumRoute>
                                        <RegisterBatchCheckoutPage />
                                    </PremiumRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-checkout/:id"
                            element={
                                <PrivateRoute>
                                    <EditCheckoutPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/quick-checkout"
                            element={
                                <PrivateRoute>
                                    <PremiumRoute>
                                        <QuickCheckoutPage />
                                    </PremiumRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/orders"
                            element={
                                <PrivateRoute>
                                    <OrdersPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/orders/:id"
                            element={
                                <PrivateRoute>
                                    <OrderDetailsPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/transactions"
                            element={
                                <PrivateRoute>
                                    <PremiumRoute>
                                        <TransactionsPage />
                                    </PremiumRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/settlement-import"
                            element={
                                <PrivateRoute>
                                    <PremiumRoute>
                                        <SettlementImportPage />
                                    </PremiumRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/create-transaction"
                            element={
                                <PrivateRoute>
                                    <PremiumRoute>
                                        <CreateTransactionPage />
                                    </PremiumRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/edit-transaction/:transactionId"
                            element={
                                <PrivateRoute>
                                    <PremiumRoute>
                                        <EditTransactionPage />
                                    </PremiumRoute>
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/calculator"
                            element={
                                <PrivateRoute>
                                    <CalculatorPage />
                                </PrivateRoute>
                            }
                        />
                    </Routes>
                </Container>
            </div>
        </ThemeProvider>
    );
}

function App() {
    return (
        <Router>
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/termos-de-uso" element={<TermsOfUsePage />} />
                <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
                <Route path="/activate-account" element={<AccountConfirmation />} />
                <Route path="/redefine-password" element={<PasswordRedefinition />} />
                <Route path="/admin/*" element={<AdminRoutes />} />
                <Route path="/calculator" element={<CalculatorPageFront />} />
            </Routes>
        </Router>
    );
}

export default App;
